import { Product, Category } from '../types';

// Use environment variable for API URL; defaults to same-origin /api in
// production (backend serves the frontend). CRA dev proxy forwards to :3001.
const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

// Log the API URL being used (helpful for debugging)
console.log('Using API URL:', API_URL);

// Product API calls
export const fetchProducts = async (params?: {
  category?: string;
  tag?: string;
  query?: string;
  limit?: number;
  offset?: number;
}): Promise<Product[]> => {
  try {
    let url = `${API_URL}/products`;
    
    if (params) {
      const queryParams = new URLSearchParams();
      
      if (params.category) queryParams.append('category', params.category);
      if (params.tag) queryParams.append('tag', params.tag);
      if (params.query) queryParams.append('query', params.query);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

export const fetchProductById = async (id: string): Promise<Product | null> => {
  try {
    const response = await fetch(`${API_URL}/products/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch product');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error fetching product ${id}:`, error);
    return null;
  }
};

// Shopify-style handle lookup
export const fetchProductByHandle = async (handle: string): Promise<Product | null> => {
  try {
    const response = await fetch(`${API_URL}/products/handle/${encodeURIComponent(handle)}`);
    if (!response.ok) throw new Error('Failed to fetch product');
    return await response.json();
  } catch (error) {
    console.error(`Error fetching product ${handle}:`, error);
    return null;
  }
};

// Canonical product URL (Shopify-style handle when available)
export const productUrl = (p: { id: string | number; handle?: string }): string =>
  `/products/${p.handle || p.id}`;

// Category API calls
export const fetchCategories = async (): Promise<Category[]> => {
  try {
    const response = await fetch(`${API_URL}/categories`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

export const fetchCategoryBySlug = async (slug: string): Promise<Category | null> => {
  try {
    const response = await fetch(`${API_URL}/categories/${slug}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch category');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error fetching category ${slug}:`, error);
    return null;
  }
};

// Tags API calls
export const fetchTags = async (): Promise<string[]> => {
  try {
    const response = await fetch(`${API_URL}/tags`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch tags');
    }
    
    const tags = await response.json();
    return tags.map((tag: { name: string }) => tag.name);
  } catch (error) {
    console.error('Error fetching tags:', error);
    return [];
  }
};